import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Upload, MessageCircle, Mail, Phone, MapPin, CheckCircle2, Send } from 'lucide-react';

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFileError('');
    
    if (!selectedFile) {
      setFile(null);
      return;
    }

    // Check file type
    const allowedTypes = [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(selectedFile.type)) {
      setFileError('Only PDF, Excel (.xls, .xlsx), and Word (.doc, .docx) files are allowed');
      setFile(null);
      e.target.value = '';
      return;
    }

    // Check file size (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setFileError('File size must be less than 5MB');
      setFile(null);
      e.target.value = '';
      return;
    }

    setFile(selectedFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('full_name', formData.full_name);
      formDataToSend.append('company_name', formData.company_name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);
      
      if (file) {
        formDataToSend.append('file', file);
      }

      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        body: formDataToSend
        // Note: Do NOT set Content-Type header, browser will set it automatically with boundary
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: data.message });
        setFormData({
          full_name: '',
          company_name: '',
          email: '',
          phone: '',
          message: ''
        });
        setFile(null);
        // Reset file input
        const fileInput = document.getElementById('product_list');
        if (fileInput) fileInput.value = '';
      } else {
        setSubmitStatus({ type: 'error', message: data.detail || 'Failed to send message' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Network error. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>Contact Us | Book Pharma Regulatory Strategy Call | TheApostrophe</title>
        <meta
          name="description"
          content="Book a regulatory strategy call with pharma export experts. Get guidance on Africa, Asia, GCC, LATAM market entry, dossier preparation, and compliance. Upload product list for assessment."
        />
        <meta name="keywords" content="pharma regulatory consultation, book regulatory strategy call, pharma export guidance, regulatory affairs consultation, dossier preparation consultation, market entry strategy, pharma compliance consulting" />
        
        <meta property="og:title" content="Contact TheApostrophe | Pharma Regulatory Consultation" />
        <meta property="og:description" content="Book a call with regulatory experts for Africa, Asia, GCC, LATAM export guidance." />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact TheApostrophe",
            "url": "https://www.theapostrophe.in/contact"
          })}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6" style={{ backgroundColor: '#606090', color: 'white' }}>
            Contact Us
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ color: '#303040' }}>
            Book a Call with Our Regulatory Team
          </h1>
          <p className="text-2xl mb-4" style={{ color: '#505060' }}>
            Your fastest path to Africa, GCC, Asia & LATAM regulatory approvals starts here.
          </p>
          <p className="text-xl mb-12" style={{ color: '#606070' }}>
            Whether you are planning a multi-country launch or need clarity on dossier readiness, our team will guide you with a structured, regulator-aligned roadmap.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
              target="_blank" 
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button size="lg" style={{ backgroundColor: '#606090', color: 'white' }} className="text-lg px-8 py-6">
                Schedule a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Button size="lg" variant="outline" className="text-lg px-8 py-6" style={{ borderColor: '#606090', color: '#606090' }} onClick={() => document.getElementById('send-us-your-details').scrollIntoView({ behavior: 'smooth' })}>
              <Upload className="mr-2 h-5 w-5" />
              Upload Your Product List
            </Button>
          </div>
        </div>
      </section>

      {/* What Happens in the Call */}
      <section className="py-20 px-6 overflow-hidden" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-center" style={{ color: '#303040' }}>
            What Happens in the Call?
          </h2>
          <p className="text-xl mb-8 text-center" style={{ color: '#606070' }}>
            A 20 to 30 minute discussion where we:
          </p>
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {[
              'Understand your product list (pharma, nutra or both)',
              'Identify priority countries based on your goals',
              'Evaluate dossier or document readiness',
              'Clarify regulatory expectations',
              'Suggest the right Success Pack or custom plan',
              'Explain timelines, next steps and expected document needs'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 p-4 rounded-lg" style={{ backgroundColor: '#F0E0D0' }}>
                <CheckCircle2 className="h-6 w-6 mt-1" style={{ color: '#606090' }} />
                <span className="text-lg" style={{ color: '#303040' }}>{item}</span>
              </div>
            ))}
          </div>
          <div className="text-center p-8 rounded-2xl" style={{ backgroundColor: 'rgba(96, 96, 144, 0.1)' }}>
            <h3 className="text-2xl font-bold mb-4" style={{ color: '#606090' }}>Outcome</h3>
            <p className="text-xl" style={{ color: '#505060' }}>
              You leave the call with a clear, country-specific regulatory strategy and an actionable plan.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="send-us-your-details" className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-center" style={{ color: '#303040' }}>
            Send Us Your Details
          </h2>
          <div className="bg-white p-8 rounded-2xl shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="full_name" className="block text-lg font-medium mb-2" style={{ color: '#303040' }}>
                  Full Name *
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:border-[#606090]"
                  style={{ borderColor: '#E0D0C0' }}
                />
              </div>

              <div>
                <label htmlFor="company_name" className="block text-lg font-medium mb-2" style={{ color: '#303040' }}>
                  Company Name *
                </label>
                <input
                  type="text"
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:border-[#606090]"
                  style={{ borderColor: '#E0D0C0' }}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-lg font-medium mb-2" style={{ color: '#303040' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:border-[#606090]"
                  style={{ borderColor: '#E0D0C0' }}
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-lg font-medium mb-2" style={{ color: '#303040' }}>
                  Phone / WhatsApp Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:border-[#606090]"
                  style={{ borderColor: '#E0D0C0' }}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-lg font-medium mb-2" style={{ color: '#303040' }}>
                  Tell us about your products *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:border-[#606090]"
                  style={{ borderColor: '#E0D0C0' }}
                  placeholder="Please describe your pharma/nutra products, target markets, and any specific regulatory questions..."
                />
              </div>

              <div>
                <label htmlFor="product_list" className="block text-lg font-medium mb-2" style={{ color: '#303040' }}>
                  Upload Product List (Optional)
                </label>
                <p className="text-sm mb-2" style={{ color: '#606070' }}>
                  Upload your product list (PDF, Excel, or Word - Max 5MB)
                </p>
                <div className="relative">
                  <input
                    type="file"
                    id="product_list"
                    name="product_list"
                    onChange={handleFileChange}
                    accept=".pdf,.xls,.xlsx,.doc,.docx"
                    className="w-full px-4 py-3 rounded-lg border-2 focus:outline-none focus:border-[#606090] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold hover:file:bg-gray-100"
                    style={{ borderColor: fileError ? '#ef4444' : '#E0D0C0' }}
                  />
                  {file && (
                    <div className="mt-2 flex items-center gap-2 text-sm" style={{ color: '#606090' }}>
                      <Upload className="h-4 w-4" />
                      <span>{file.name} ({(file.size / 1024).toFixed(1)} KB)</span>
                    </div>
                  )}
                  {fileError && (
                    <p className="mt-2 text-sm text-red-600">{fileError}</p>
                  )}
                </div>
              </div>

              {submitStatus && (
                <div className={`p-4 rounded-lg ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  <p className="text-center font-medium">{submitStatus.message}</p>
                </div>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full text-lg py-6"
                style={{ backgroundColor: '#606090', color: 'white' }}
              >
                {isSubmitting ? 'Sending...' : 'Book My Call'}
                {!isSubmitting && <Send className="ml-2 h-5 w-5" />}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* WhatsApp Quick Connect */}
      <section className="py-20 px-6 overflow-hidden" style={{ backgroundColor: 'white' }}>
        <div className="max-w-4xl mx-auto text-center">
          <MessageCircle className="h-16 w-16 mx-auto mb-6" style={{ color: '#25D366' }} />
          <h2 className="text-4xl font-bold mb-6" style={{ color: '#303040' }}>
            Prefer WhatsApp? Connect Instantly.
          </h2>
          <p className="text-xl mb-8" style={{ color: '#606070' }}>
            If you want a quicker response, you can message our regulatory desk directly. Attach your product list, ask a query, or request a callback.
          </p>
          <a href="https://wa.me/919714485551" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="text-lg px-8 py-6" style={{ backgroundColor: '#25D366', color: 'white' }}>
              <MessageCircle className="mr-2 h-5 w-5" />
              Click to Chat
            </Button>
          </a>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            Reach Us
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-white rounded-2xl shadow-md">
              <Mail className="h-12 w-12 mx-auto mb-4" style={{ color: '#606090' }} />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#303040' }}>Email</h3>
              <p style={{ color: '#606070' }}>
                <a href="mailto:purva.shah@theapostrophe.in" className="block hover:underline">purva.shah@theapostrophe.in</a>
                <a href="mailto:hello@theapostrophe.in" className="block hover:underline mt-1">hello@theapostrophe.in</a>
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-2xl shadow-md">
              <Phone className="h-12 w-12 mx-auto mb-4" style={{ color: '#606090' }} />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#303040' }}>Phone / WhatsApp</h3>
              <p style={{ color: '#606070' }}>
                <a href="tel:+919714485551" className="hover:underline">+91-9714485551</a>
              </p>
            </div>

            <div className="text-center p-6 bg-white rounded-2xl shadow-md">
              <MapPin className="h-12 w-12 mx-auto mb-4" style={{ color: '#606090' }} />
              <h3 className="text-xl font-bold mb-3" style={{ color: '#303040' }}>Website</h3>
              <p style={{ color: '#606070' }}>
                <a href="https://www.theapostrophe.in" target="_blank" rel="noopener noreferrer" className="hover:underline">www.theapostrophe.in</a>
              </p>
            </div>
          </div>
          <p className="text-center mt-8 text-lg" style={{ color: '#606090' }}>
            <strong>Our team replies within 24 hours on business days.</strong>
          </p>
        </div>
      </section>

      {/* Office Location */}
      <section className="py-20 px-6 overflow-hidden" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-6 text-center" style={{ color: '#303040' }}>
            Our Office
          </h2>
          <p className="text-xl mb-8 text-center" style={{ color: '#606070' }}>
            You are welcome to schedule an in-person meeting or virtual session with our regulatory team.
          </p>
          <div className="bg-white p-8 rounded-2xl shadow-md text-center">
            <MapPin className="h-12 w-12 mx-auto mb-4" style={{ color: '#606090' }} />
            <p className="text-xl font-medium" style={{ color: '#303040' }}>
              B-319, Aarohi Galleria<br />
              South Bopal<br />
              Ahmedabad 380058, India
            </p>
          </div>
        </div>
      </section>

      {/* Why Companies Choose TheApostrophe */}
      <section className="py-20 px-6 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold mb-12 text-center" style={{ color: '#303040' }}>
            Why Companies Choose TheApostrophe
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: 'Multi-country expertise', desc: 'Across 30+ emerging markets' },
              { title: 'Founder-led execution', desc: 'Direct regulatory expertise' },
              { title: 'Clear checklists', desc: 'Predictable workflow' },
              { title: 'Dedicated RA partner', desc: 'For exporters at every stage' }
            ].map((reason, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-md">
                <CheckCircle2 className="h-8 w-8 mb-4" style={{ color: '#606090' }} />
                <h3 className="text-2xl font-bold mb-2" style={{ color: '#303040' }}>{reason.title}</h3>
                <p className="text-lg" style={{ color: '#606070' }}>{reason.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 overflow-hidden" style={{ background: 'linear-gradient(135deg, #606090 0%, #7080A0 100%)' }}>
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to start your registrations?
          </h2>
          <p className="text-2xl mb-8 text-white/90">
            Share your product list or schedule a call - we will take care of the rest.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true" 
              target="_blank" 
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button size="lg" className="text-lg px-8 py-6 bg-white hover:bg-white/90" style={{ color: '#606090' }}>
                Book a Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Button size="lg" variant="outline" className="text-lg px-8 py-6 border-2 border-white text-white hover:bg-white/10" onClick={() => document.getElementById('send-us-your-details').scrollIntoView({ behavior: 'smooth' })}>
              <Upload className="mr-2 h-5 w-5" />
              Upload Your Product List
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
